import { useRef, useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { Briefcase, MessageCircle, Users, Brain, Globe, Award, User, Building2, GraduationCap, Compass, Mic, Crown, Shield, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import heroAvif480 from "@/assets/hero-bg-480.avif";
import heroAvif960 from "@/assets/hero-bg-960.avif";
import heroAvif1600 from "@/assets/hero-bg-1600.avif";
import heroWebp480 from "@/assets/hero-bg-480.webp";
import heroWebp960 from "@/assets/hero-bg-960.webp";
import heroWebp1600Asset from "@/assets/hero-bg-1600.webp.asset.json";
const heroWebp1600 = heroWebp1600Asset.url;
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
const FounderSection = lazy(() => import("@/components/home/FounderSection"));
const TestimonialsSection = lazy(() => import("@/components/home/TestimonialsSection"));
const FAQSection = lazy(() => import("@/components/home/FAQSection"));
const BlogPreviewSection = lazy(() => import("@/components/home/BlogPreviewSection"));
const ContactCTASection = lazy(() => import("@/components/home/ContactCTASection"));
import { useSitePage, usePublishedCaseStudies } from "@/lib/cms";
import { getRecord, getRecordArray, getString } from "@/lib/pageContent";
import { MVIBE_ORG, buildBreadcrumb, buildFAQ, CORE_FAQS } from "@/lib/seo/schemas";

const audienceCards = [
  {
    icon: User,
    audience: "For Individuals",
    promise: "One-on-one coaching for working professionals seeking clarity, confidence and career alignment.",
    modules: ["Life & career coaching", "Personal branding", "Image & style consulting"],
    href: "/services/individuals",
    ctaLabel: "Explore individual coaching",
  },
  {
    icon: Building2,
    audience: "For Companies",
    promise: "Executive facilitation and leadership development that turns teams into confident decision-makers.",
    modules: ["Executive coaching & facilitation", "Leadership & soft-skills training", "Certified POSH programmes"],
    href: "/services/companies",
    ctaLabel: "Explore corporate programmes",
  },
  {
    icon: GraduationCap,
    audience: "For Students",
    promise: "Campus-to-corporate workshops that make final-year students genuinely job-ready.",
    modules: ["Communication & presentation", "Interview & CV coaching", "Workplace etiquette"],
    href: "/services/students",
    ctaLabel: "Explore student programmes",
  },
];

const outcomeChips = [
  { icon: Compass, label: "Clarity of direction" },
  { icon: Mic, label: "Confident communication" },
  { icon: Crown, label: "Executive presence" },
  { icon: GraduationCap, label: "Career-ready graduates" },
  { icon: Shield, label: "Resilience under pressure" },
  { icon: Sparkles, label: "Authentic leadership" },
];

const credentials = [
  "ICF-aligned Coach",
  "NLP Practitioner",
  "ART Therapy",
  "Certified POSH Trainer",
  "NABET Soft Skills Trainer",
  "City & Guilds",
];

const CountUp = ({ target, suffix = "" }: { target: number; suffix?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const count = useMotionValue(0);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, target, {
      duration: 2,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v).toString()),
    });
    return controls.stop;
  }, [isInView, target, count]);

  return <span ref={ref}>{display}{suffix}</span>;
};

const fallbackStats = [
  { value: 300, suffix: "+", label: "Hours of Coaching" },
  { value: 15, suffix: "+", label: "Years Corporate Experience" },
  { value: 150, suffix: "+", label: "Training Delivered" },
  { value: 7, suffix: "", label: "Professional Certifications" },
];


const differentiatorIcons = [Briefcase, MessageCircle, Users, Brain, Globe, Award];
const fallbackDifferentiators = [
  { title: "15+ Years of Corporate Insight", desc: "Our founder's deep IT HR leadership experience gives a rare understanding of corporate dynamics, pressures, and opportunities." },
  { title: "Coaching-Led Facilitation", desc: "Every session blends reflective coaching with directive facilitation, balancing guidance with self-discovery." },
  { title: "Tailored to Your Audience", desc: "CXO leadership offsites, student campus workshops, or individual coaching — each programme is adapted to the audience." },
  { title: "Mental Fitness & Resilience Focus", desc: "Practical strategies for stress management, overcoming limiting beliefs, and building mental strength for demanding environments." },
  { title: "India & UAE Presence", desc: "Serving professionals and organisations across India and Abu Dhabi with face-to-face and virtual delivery." },
  { title: "Certified & Credentialed", desc: "ICF-aligned Coach, NLP Practitioner, ART Therapy, Certified POSH Trainer, NABET Soft Skills Trainer, City & Guild." },
];

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: "easeOut" as const },
});

const Home = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { data: page } = useSitePage("home");
  const content = (page?.content ?? {}) as Record<string, unknown>;


  const stats = getRecordArray<Record<string, unknown>>(content.stats, fallbackStats).map((item, index) => ({
    value: Number(item.value ?? fallbackStats[index]?.value ?? 0),
    suffix: typeof item.suffix === "string" ? item.suffix : fallbackStats[index]?.suffix ?? "",
    label: getString(item.label, fallbackStats[index]?.label ?? ""),
  }));

  const { data: caseStudies = [] } = usePublishedCaseStudies();
  const featuredCaseStudies = [...caseStudies]
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    .slice(0, 3);


  const differentiators = getRecordArray<Record<string, unknown>>(content.differentiators, fallbackDifferentiators).map((item, index) => ({
    title: getString(item.title, fallbackDifferentiators[index]?.title ?? ""),
    desc: getString(item.desc, fallbackDifferentiators[index]?.desc ?? ""),
  }));

  const founderContent = getRecord(content.founder, {});
  const testimonialsContent = getRecord(content.testimonials, {});
  const faqContent = getRecord(content.faqs, {});
  const contactCtaContent = getRecord(content.contactCta, {});

  const homeSchema = page?.seo.schema ?? [
    MVIBE_ORG,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: "https://mvibeon.com/",
      name: "MVIBE",
      publisher: { "@id": "https://mvibeon.com/#organization" },
      potentialAction: {
        "@type": "SearchAction",
        target: "https://mvibeon.com/blog?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    buildBreadcrumb([{ name: "Home", path: "/" }]),
    buildFAQ(CORE_FAQS),
  ];

  return (
    <>
      <SEOHead
        title={page?.seo.title ?? "Executive Coaching & Soft Skills Training in Dubai | MVIBE"}
        description={page?.seo.description ?? "ICF-certified executive coaching and corporate soft skills training in Dubai, led by Mahirah — 15+ years in IT HR leadership."}
        canonical={page?.seo.canonical}
        ogTitle={page?.seo.ogTitle}
        ogDescription={page?.seo.ogDescription}
        ogImage={page?.seo.ogImage}
        type={page?.seo.type ?? "website"}
        schema={homeSchema}
        preloadImages={[{
          href: heroAvif480,
          imagesrcset: `${heroAvif480} 480w, ${heroAvif960} 960w, ${heroAvif1600} 1600w`,
          imagesizes: "(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw",
          type: "image/avif",
        }]}
      />


      <section ref={heroRef} className="relative flex min-h-[70vh] md:min-h-[90vh] items-center overflow-hidden">
        <picture className="absolute inset-0 block h-full w-full">
          <source
            type="image/avif"
            srcSet={`${heroAvif480} 480w, ${heroAvif960} 960w, ${heroAvif1600} 1600w`}
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet={`${heroWebp480} 480w, ${heroWebp960} 960w, ${heroWebp1600} 1600w`}
            sizes="100vw"
          />
          <img
            src={heroWebp480}
            srcSet={`${heroWebp480} 480w, ${heroWebp960} 960w, ${heroWebp1600} 1600w`}
            sizes="100vw"
            alt=""
            width={1600}
            height={1067}
            {...({ fetchpriority: "high" } as Record<string, string>)}
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/75" />


        <div className="container relative z-10 mx-auto px-4 py-24 md:py-0">
          <div className="max-w-2xl text-center md:text-left">
            <p className="mb-4 font-sans text-base uppercase tracking-[0.25em] text-accent">
              {getString(content.heroEyebrow, "Coaching · Training · Consulting")}
            </p>
            <h1 className="text-3xl font-heading font-bold leading-tight text-primary-foreground md:text-5xl lg:text-6xl">
              {getString(content.heroTitle, "Unlock Your Full Potential. Lead with Clarity. Achieve Lasting Success.")}
            </h1>
            <p className="mt-4 max-w-xl font-sans text-lg text-primary-foreground/95">
              {getString(content.heroDescription, "MVIBE empowers individuals and organisations to cultivate inner clarity and achieve breakthrough results through personalized coaching, transformative training, and strategic consulting.")}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 md:justify-start">
              <DiscoveryCallDialog>
                <Button size="lg" className="bg-accent font-semibold text-primary hover:bg-accent/90">
                  Book a Discovery Call
                </Button>
              </DiscoveryCallDialog>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                <Link to="/services">Explore Our Services</Link>
              </Button>
            </div>
          </div>
        </div>

      </section>

      <section className="bg-secondary py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-heading font-bold text-accent md:text-5xl">
                  <CountUp target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="mt-1 font-sans text-base text-secondary-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-14">
        <div className="container mx-auto px-4">
          <div className="mb-8 text-center">
            <p className="mb-2 font-sans text-sm uppercase tracking-[0.25em] text-primary">Who we work with</p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-3xl font-heading font-bold text-foreground md:text-5xl">
              {getString(content.servicesSectionTitle, "Coaching and training, tailored to your stage")}
            </motion.h2>
          </div>

          <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3">
            {audienceCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.audience}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="flex flex-col rounded-xl border border-border border-l-4 border-l-accent bg-card p-6"
                >
                  <Icon className="mb-3 h-8 w-8 text-accent" />
                  <h3 className="mb-2 text-xl font-heading font-bold text-foreground">{card.audience}</h3>
                  <p className="mb-4 font-sans text-base leading-relaxed text-muted-foreground">{card.promise}</p>
                  <ul className="mb-5 space-y-1.5 font-sans text-sm text-foreground/90">
                    {card.modules.map((m) => (
                      <li key={m} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={card.href} className="mt-auto inline-flex min-h-11 items-center gap-1 py-2 font-sans text-sm font-semibold text-primary hover:text-accent-strong">
                    {card.ctaLabel} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.4 }} className="mt-8 text-center">
            <Button asChild size="lg" className="bg-accent font-semibold text-primary hover:bg-accent/90">
              <Link to="/services">{getString(content.servicesCtaLabel, "Explore all services")}</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="bg-secondary/50 py-10">
        <div className="container mx-auto px-4">
          <p className="mb-6 text-center font-sans text-sm uppercase tracking-[0.25em] text-primary">Outcomes clients walk away with</p>
          <div className="mx-auto flex max-w-5xl flex-wrap justify-center gap-3">
            {outcomeChips.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 font-sans text-sm text-foreground">
                <Icon className="h-4 w-4 text-accent" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-10">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl rounded-xl border border-border bg-card p-6 md:p-8 text-center">
            <p className="mb-3 font-sans text-sm uppercase tracking-[0.25em] text-primary">Credentials</p>
            <div className="flex flex-wrap justify-center gap-2">
              {credentials.map((c) => (
                <span key={c} className="rounded-md border border-accent/30 bg-accent/5 px-3 py-1 font-sans text-sm text-foreground">
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-4 font-sans text-base text-muted-foreground">
              15+ years in IT HR leadership before founding MVIBE.
            </p>
          </div>
        </div>
      </section>

      {featuredCaseStudies.length >= 2 && (
        <section className="bg-muted/40 py-14">
          <div className="container mx-auto px-4">
            <div className="mb-8 text-center">
              <p className="mb-2 font-sans text-sm uppercase tracking-[0.25em] text-primary">Client results</p>
              <h2 className="text-3xl font-heading font-bold text-foreground md:text-4xl">
                Real engagements, real outcomes
              </h2>
            </div>
            <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3">
              {featuredCaseStudies.map((cs, i) => (
                <motion.article
                  key={cs.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex flex-col rounded-xl border border-border bg-card p-6"
                >
                  {cs.category && (
                    <p className="mb-2 font-sans text-xs uppercase tracking-widest text-accent">{cs.category}</p>
                  )}
                  <h3 className="mb-3 text-lg font-heading font-bold text-foreground">{cs.title}</h3>
                  <p className="mb-4 font-sans text-sm leading-relaxed text-muted-foreground line-clamp-4">{cs.summary}</p>
                  {cs.outcomes?.[0] && (
                    <p className="mb-4 font-sans text-sm text-foreground">
                      <span className="font-semibold text-accent-strong">{cs.outcomes[0].value}</span>{" "}
                      {cs.outcomes[0].label}
                    </p>
                  )}
                  <Link to={`/case-studies/${cs.slug}`} className="mt-auto inline-flex min-h-11 items-center gap-1 py-2 font-sans text-sm font-semibold text-primary hover:text-accent-strong" aria-label={`Read case study: ${cs.title}`}>
                    Read case study <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </motion.article>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button asChild variant="outline" size="lg">
                <Link to="/case-studies">See all case studies</Link>
              </Button>
            </div>
          </div>
        </section>
      )}


      <section className="bg-muted/40 py-10">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-6 max-w-2xl text-center">
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-2 font-sans text-base uppercase tracking-[0.2em] text-primary">
              {getString(content.whyEyebrow, "Why Us · MVIBE Advantage")}
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="text-3xl font-heading font-bold text-foreground md:text-5xl">
              {getString(content.whyTitle, "Clarity within is the reason transformation sticks")}
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-3 font-sans text-lg text-muted-foreground">
              {getString(content.whyDescription, "We don't deliver generic workshops. We craft personalized pathways to leadership, confidence, and lasting change — for individuals and organisations alike.")}
            </motion.p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 md:grid-cols-3">
            {differentiators.map((item, i) => {
              const Icon = differentiatorIcons[i] ?? Briefcase;
              return (
                <motion.div key={item.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} whileHover={{ y: -4 }} className="rounded-xl border border-border bg-card p-5 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent/15">
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="mb-1.5 text-lg font-heading font-bold text-foreground">{item.title}</h3>
                  <p className="font-sans text-base leading-relaxed text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
        <FounderSection content={founderContent} />
      </Suspense>
      <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
        <TestimonialsSection content={testimonialsContent} />
      </Suspense>

      <section className="bg-background py-14">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-3 text-3xl font-heading font-bold text-foreground md:text-4xl">
              MVIBE by the numbers
            </h2>
            <p className="mb-6 font-sans text-base text-muted-foreground">
              A snapshot of the coaching hours, training delivered and audiences reached across UAE and India.
            </p>
          </div>
          <div className="mx-auto max-w-3xl overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left font-sans text-base">
              <caption className="sr-only">MVIBE coaching and training metrics</caption>
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th scope="col" className="px-4 py-3 font-heading">Metric</th>
                  <th scope="col" className="px-4 py-3 font-heading">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr><th scope="row" className="px-4 py-3 font-normal">Courses &amp; programmes</th><td className="px-4 py-3 font-heading text-accent-strong">12+</td></tr>
                <tr><th scope="row" className="px-4 py-3 font-normal">Professionals reached</th><td className="px-4 py-3 font-heading text-accent-strong">3,000+</td></tr>
                <tr><th scope="row" className="px-4 py-3 font-normal">Corporates &amp; colleges served</th><td className="px-4 py-3 font-heading text-accent-strong">50+</td></tr>
                <tr><th scope="row" className="px-4 py-3 font-normal">Coaching hours</th><td className="px-4 py-3 font-heading text-accent-strong">300+</td></tr>
                <tr><th scope="row" className="px-4 py-3 font-normal">Training delivered</th><td className="px-4 py-3 font-heading text-accent-strong">150+</td></tr>
                <tr><th scope="row" className="px-4 py-3 font-normal">Years of corporate experience</th><td className="px-4 py-3 font-heading text-accent-strong">15+</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
        <FAQSection content={faqContent} />
      </Suspense>
      <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
        <BlogPreviewSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
        <ContactCTASection content={contactCtaContent} />
      </Suspense>
    </>
  );
};

export default Home;
